import { Clock, Gem, Play } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Challenge, Submission } from '@jemmi/schemas';

import { challengeCoverColors } from '@/lib/mockData';
import { cn } from '@/lib/cn';
import { useCountdown } from '@/lib/useCountdown';

type Props = {
  challenge: Challenge;
  leadingSubmission?: Submission | null;
  onVote?: (challengeId: string) => void;
};

/**
 * Card that displays a single challenge in the feed.
 *
 * The whole card is a link to the challenge detail page. The vote button
 * stops event propagation so tapping "vote" doesn't navigate.
 *
 * If a leading submission is provided, it's displayed as a thumbnail
 * instead of the gradient background.
 */
export function ChallengeCard({ challenge, leadingSubmission, onVote }: Props) {
  const countdown = useCountdown(challenge.endsAt);
  const [voted, setVoted] = useState(challenge.hasVoted);
  const [animatingVote, setAnimatingVote] = useState(false);

  const coverColor = challengeCoverColors[challenge.id] ?? 'from-slate-400 to-slate-600';

  const handleVote = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (voted || countdown.ended) return;
    setVoted(true);
    setAnimatingVote(true);
    window.setTimeout(() => setAnimatingVote(false), 700);
    onVote?.(challenge.id);
  };

  const displayedJems = challenge.jemCount + (voted && !challenge.hasVoted ? 1 : 0);

  return (
    <Link
      to={`/challenge/${challenge.id}`}
      className="group relative block overflow-hidden rounded-2xl border border-slate-200/80 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-brand-md"
    >
      {/* Cover section: thumbnail or gradient background */}
      <div className="relative h-40 overflow-hidden bg-gradient-to-br">
        {leadingSubmission ? (
          <>
            {/* Submission thumbnail image */}
            <img
              src={leadingSubmission.mediaUrl}
              alt={leadingSubmission.caption || 'Submission'}
              className="h-full w-full object-cover"
            />
            {/* Dark overlay for better readability of text badges */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            {/* Play icon overlay for videos */}
            {leadingSubmission.mediaType === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm">
                  <Play className="h-6 w-6 fill-slate-700 text-slate-700" />
                </div>
              </div>
            )}
          </>
        ) : (
          /* Default gradient background if no submission */
          <div className={cn('h-full w-full bg-gradient-to-br', coverColor)}>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.4),_transparent_60%)]" />
          </div>
        )}

        {/* Category badge */}
        <div className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-700 backdrop-blur-sm">
          {challenge.category}
        </div>

        {/* Countdown timer */}
        <div
          className={cn(
            'absolute right-3 top-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
            countdown.urgent
              ? 'bg-red-500 text-white'
              : 'bg-white/90 text-slate-700 backdrop-blur-sm',
          )}
        >
          <Clock className="h-3 w-3" />
          {countdown.label}
        </div>
      </div>

      {/* Content section */}
      <div className="p-4">
        <h3 className="mb-1.5 line-clamp-2 font-semibold leading-snug text-slate-900">
          {challenge.title}
        </h3>
        <p className="mb-3 line-clamp-2 min-h-[2.5rem] text-sm text-slate-600">
          {challenge.brief}
        </p>

        {/* Footer with creator and vote button */}
        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-300 to-brand-600 text-[10px] font-semibold text-white">
              {challenge.creator.displayName
                .split(' ')
                .map((p) => p[0])
                .slice(0, 2)
                .join('')}
            </div>
            <span className="text-xs text-slate-600">{challenge.creator.displayName}</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs tabular-nums text-slate-500">
              {challenge.submissionCount} entries
            </span>

            <button
              type="button"
              onClick={handleVote}
              disabled={voted || countdown.ended}
              aria-label={voted ? 'You voted' : 'Vote with a jem'}
              className={cn(
                'relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
                voted
                  ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200'
                  : countdown.ended
                    ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                    : 'bg-gradient-to-br from-brand-500 to-brand-700 text-white hover:shadow-brand-md active:scale-95',
              )}
            >
              <Gem
                className={cn('h-3.5 w-3.5', animatingVote && 'animate-bounce')}
                fill={voted ? '#1E6BE6' : 'currentColor'}
              />
              <span className="tabular-nums">{displayedJems}</span>
              {animatingVote && (
                <span className="absolute -right-1 -top-2 animate-ping text-[10px] font-bold text-brand-600">
                  +1
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
